/**
 * Displays autocomplete suggestions for an input field when its value changes and it has focus.
 *
 * @class Autocomplete
 * @author EN GARDE (Martin Holler)
 * @version 1.1
 */

var EN = EN || {};

EN.Autocomplete = (function (_, $) {
	'use strict';

	/**
	 * @constructor
	 * @param {jQuery} $input - Input after which to add the autocomplete element.
	 * @param {object} options
	 * @property {string} options.ajaxUrl - URL that returns the autocomplete data.
	 * @property {string} options.type - Type of the data to return.
	 * @property {number} options.debounceRate=300 - Milliseconds that need to pass until the autocomplete element is shown.
	 * @property {number} options.minCharacters=3 - Minimum number of characters that the input needs to have before the autocomplete element is shown.
	 * @property {object} classNames - Collection of class names set by the class.
	 * @return {InView}
	 */
	function Autocomplete($input, options) {
		var that = this;

		if (!$input || !$input.length) {
			return that;
		}

		that.options = _.defaultsDeep(options, that.defaults);
		that.$input = $input.first();
		that.$items = $([]);
		that.xhrRequest = undefined;

		if (!that.options.ajaxUrl) {
			console.error('[Autocomplete] No Ajax URL was specified for the following field: ', $input.get(0));
			return that;
		}

		that.createElement();

		// event handlers setup
		that.$input.on('focus', function () {
			that.show();
		});

		that.$input.on('blur', function () {
			setTimeout(function () {
				that.hide();
			}, 100); // allow clicks
		});

		that.$input.on('input', _.debounce(function () {
			if (that.$input.val().length >= that.options.minCharacters) {
				that.update();
			}
			else {
				that.hide();
				that.clear();
			}
		}, that.options.debounceRate));

		that.$input.on('keydown', function (e) {
			var targetIndex;
			var $targetElement;

			switch (e.keyCode) {
				case 38: // UP
				case 40: // DOWN
					targetIndex = that.$items.index(that.$items.filter('.' + that.options.classNames.active)) + (e.keyCode === 38 ? -1 : 1);

					if (targetIndex === that.$items.length) {
						targetIndex = 0;
					}

					that.$items.removeClass(that.options.classNames.active);
					that.$items.eq(targetIndex).addClass(that.options.classNames.active);

					e.preventDefault();
					break;
				case 27: // ESC
					that.hide();
					break;
				case 13: // ENTER
					$targetElement = that.$items.filter('.' + that.options.classNames.active);

					if ($targetElement.length) {
						$targetElement.get(0).click(); // jQuery's .trigger() doesn't work here
						that.hide();

						e.preventDefault();
					}
					break;
			}
		});

		that.$element.on('click', 'span.' + that.options.classNames.item, function () {
			that.$input.val($(this).text()).trigger('change');
		});
	}

	// defaults
	Autocomplete.prototype.defaults = {
		ajaxUrl: undefined,
		debounceRate: 300,
		minCharacters: 3,
		classNames: {
			element: 'en-autocomplete',
			heading: 'en-autocomplete__heading',
			item: 'en-autocomplete__item',
			active: 'en-is-active',
			hidden: 'en-hidden'
		}
	};

	/**
	 * Creates the autocomplete element if it doesn't exist already.
	 * @memberof Autocomplete
	 * @return {Autocomplete}
	 */
	Autocomplete.prototype.createElement = function () {
		var that = this;

		if (that.$element) {
			return that.$element;
		}

		that.$element = $('<div class="' + that.options.classNames.element + '"></div>');
		that.$input.after(that.$element);
		that.hide();

		return that;
	};

	/**
	 * Creates autocomplete items for a given config.
	 * The config can either be a flat array of items, or a nested array of items.
	 * @memberof Autocomplete
	 * @param {object[]} config
	 * @return {Autocomplete}
	 */
	Autocomplete.prototype.createItems = function (config) {
		var that = this;
		var html = '';
		var doCreate = function (config) {
			_.each(config, function (itemConfig) {
				if (itemConfig.label) {
					if (itemConfig.url) {
						html += '<a class="' + that.options.classNames.item + '" href="' + itemConfig.url + '">' + itemConfig.label + '</a>';
					}
					else {
						html += '<span class="' + (itemConfig.items ? that.options.classNames.heading : that.options.classNames.item) + '">' + itemConfig.label + '</span>';
					}
				}

				if (itemConfig.items) {
					doCreate(itemConfig.items);
				}
			});
		};

		if (!config || !config.length) {
			return that;
		}

		doCreate(config);

		that.$element.html(html);
		that.$items = that.$element.find('.' + that.options.classNames.item);

		return that;
	};

	/**
	 * Shows the autocomplete element if there are items to display.
	 * @memberof Autocomplete
	 * @return {Autocomplete}
	 */
	Autocomplete.prototype.show = function () {
		var that = this;

		if (!that.$element.is(':empty')) {
			that.$element.removeClass(that.options.classNames.hidden);
		}

		return that;
	};

	/**
	 * Hides the autocomplete element.
	 * @memberof Autocomplete
	 * @return {Autocomplete}
	 */
	Autocomplete.prototype.hide = function () {
		var that = this;

		that.$element.addClass(that.options.classNames.hidden);
		that.$items.removeClass(that.options.classNames.active);

		return that;
	};

	/**
	 * Deletes all autocomplete items from the element.
	 * @memberof Autocomplete
	 * @return {Autocomplete}
	 */
	Autocomplete.prototype.clear = function () {
		var that = this;

		that.$element.empty();
		that.$items = $([]);

		return that;
	};

	/**
	 * Requests new content to be displayed in the autocomplete element for the value that's currently in the input field.
	 * @memberof Autocomplete
	 * @param {function} [callback] - Function to be called after new content is requested.
	 * @return {Autocomplete}
	 */
	Autocomplete.prototype.update = function (callback) {
		var that = this;
		var value = that.$input.val();

		if (!value) {
			return that;
		}

		// abort running request
		if (that.xhrRequest && that.xhrRequest.readystate !== 4) {
			that.xhrRequest.abort();
		}

		// run the ajax request
		that.xhrRequest = $.ajax({
			url: that.options.ajaxUrl,
			data: {
				value: value,
				type: that.options.type
			}
		}).always(function (data) {
			if (typeof data === 'object' && data.success && data.items && data.items.length) {
				that.createItems(data.items);

				if (that.$input.is(':focus')) {
					that.show();
				}

				if (typeof callback === 'function') {
					callback(data);
				}
			}
			else {
				that.hide();
				that.clear();
			}
		});

		return that;
	};

	return Autocomplete;
}(lodash, jQuery));
